import { useMemo, useState } from 'react';
import { Download, Terminal, XCircle, Filter } from 'lucide-react';
import type { Campaign, PipelineEvent, Run } from '../../../../shared/types';
import { useCollection, useRunLog } from '../../lib/hooks';
import { cancelRun, downloadRunLog, errorText } from '../../lib/api';
import { formatDuration, formatWhen, parseDate } from '../../lib/pb';
import { confirmDialog } from '../ConfirmDialog';

const STATUS_STYLE: Record<string, string> = {
  running: 'bg-natural-sage/15 text-natural-sage',
  cancelling: 'bg-amber-50 text-amber-700',
  succeeded: 'bg-emerald-50 text-emerald-700',
  failed: 'bg-red-50 text-red-600',
  cancelled: 'bg-stone-100 text-natural-text/60',
};

const LEVEL_STYLE: Record<string, string> = {
  debug: 'text-natural-text/40',
  info: 'text-natural-text/75',
  warn: 'text-amber-700',
  error: 'text-red-600',
};

/**
 * Every run ever, and the full log of each.
 *
 * This is the replacement for n8n's execution list — with the crucial difference
 * that a cancelled or crashed run is just as readable as a successful one,
 * because events are written as they happen rather than serialised at the end.
 */
export default function RunHistory() {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const runs = useCollection<Run>('runs', { sort: '-created', expand: 'campaign,started_by' });
  const events = useRunLog(selectedId);

  const filtered = useMemo(
    () => (statusFilter ? runs.items.filter((r) => r.status === statusFilter) : runs.items),
    [runs.items, statusFilter]
  );

  const selected = runs.items.find((r) => r.id === selectedId) ?? null;
  const selectedCampaign = selected?.expand?.campaign as Campaign | undefined;

  const elapsed = (run: Run) => {
    const started = parseDate(run.started);
    if (!started) return '—';
    const ended = parseDate(run.ended) ?? new Date();
    return formatDuration(ended.getTime() - started.getTime());
  };

  return (
    <div className="space-y-4">
      <div className="zen-card p-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-serif text-lg font-medium">Run history</h3>
          <p className="text-[11px] text-natural-text/55 mt-0.5">
            {runs.items.length} run{runs.items.length === 1 ? '' : 's'}. Logs are written as each
            step happens, so cancelled and crashed runs are complete up to the moment they stopped.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Filter size={13} className="text-natural-text/35" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="zen-input py-1.5 text-xs w-36"
          >
            <option value="">All statuses</option>
            <option value="running">Running</option>
            <option value="succeeded">Succeeded</option>
            <option value="failed">Failed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {error && <div className="zen-card p-4 text-xs text-red-600">{error}</div>}

      <div className="grid lg:grid-cols-3 gap-4 items-start">
        <div className="zen-card p-3 space-y-1 max-h-[600px] overflow-y-auto">
          {runs.loading && <p className="text-xs text-natural-text/40 p-3">Loading…</p>}
          {!runs.loading && filtered.length === 0 && (
            <p className="text-xs text-natural-text/40 p-6 text-center">No runs to show.</p>
          )}

          {filtered.map((run) => {
            const campaign = run.expand?.campaign as Campaign | undefined;
            return (
              <button
                key={run.id}
                onClick={() => setSelectedId(run.id)}
                className={`w-full text-left p-3 rounded-xl transition-colors cursor-pointer ${
                  run.id === selectedId ? 'bg-natural-sage/10' : 'hover:bg-natural-bg'
                }`}
              >
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="text-xs font-semibold truncate">
                    #{campaign?.seq ?? '?'} · run {run.run_number}
                  </span>
                  <span className={`zen-pill ${STATUS_STYLE[run.status] ?? ''}`}>{run.status}</span>
                </div>
                <p className="text-[10px] text-natural-text/50 truncate">
                  {campaign?.title || 'Untitled'} · {elapsed(run)} · {formatWhen(run.started)}
                </p>
              </button>
            );
          })}
        </div>

        <div className="lg:col-span-2 zen-card overflow-hidden">
          {!selected ? (
            <p className="text-xs text-natural-text/45 p-10 text-center">
              Choose a run to read its log.
            </p>
          ) : (
            <>
              <div className="p-5 border-b border-natural-border space-y-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h4 className="font-serif text-base font-medium">
                      Campaign #{selectedCampaign?.seq ?? '?'} · run {selected.run_number}
                    </h4>
                    <p className="text-[11px] text-natural-text/55 mt-0.5">{selected.trigger}</p>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={async () => {
                        try {
                          await downloadRunLog(
                            selected.id,
                            `run-${selectedCampaign?.seq ?? 'x'}-${selected.run_number}.txt`
                          );
                        } catch (err) {
                          setError(errorText(err));
                        }
                      }}
                      className="zen-btn-ghost py-1.5 px-3 text-[11px]"
                    >
                      <Download size={12} />
                      Download .txt
                    </button>

                    {(selected.status === 'running' || selected.status === 'cancelling') && (
                      <button
                        onClick={async () => {
                          if (
                            !(await confirmDialog({
                              title: 'Cancel this run?',
                              message: 'Work already finished is kept — only the remaining steps stop.',
                              confirmLabel: 'Cancel the run',
                              cancelLabel: 'Keep running',
                              danger: true,
                            }))
                          ) {
                            return;
                          }
                          setBusy(true);
                          try {
                            await cancelRun(selected.id);
                            await runs.reload();
                          } catch (err) {
                            setError(errorText(err));
                          } finally {
                            setBusy(false);
                          }
                        }}
                        disabled={busy}
                        className="zen-btn-danger py-1.5 px-3 text-[11px]"
                      >
                        <XCircle size={12} />
                        Cancel
                      </button>
                    )}
                  </div>
                </div>

                {selected.stats && Object.keys(selected.stats).length > 0 && (
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-natural-text/60 font-mono">
                    {Object.entries(selected.stats).map(([key, value]) => (
                      <span key={key}>
                        {key.replace(/_/g, ' ')}: <strong>{String(value)}</strong>
                      </span>
                    ))}
                  </div>
                )}

                {selected.error && (
                  <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-xl p-3 break-words">
                    {selected.error}
                  </p>
                )}
              </div>

              <div className="bg-stone-50/60 max-h-[420px] overflow-y-auto font-mono text-[11px] leading-relaxed p-4 space-y-0.5">
                {events.loading && <p className="text-natural-text/40">Loading log…</p>}
                {!events.loading && events.items.length === 0 && (
                  <p className="text-natural-text/40 flex items-center gap-2">
                    <Terminal size={12} />
                    No events were recorded for this run.
                  </p>
                )}

                {(events.items as PipelineEvent[]).map((ev) => {
                  const time = parseDate(ev.created);
                  return (
                    <div key={ev.id} className="flex gap-2">
                      <span className="text-natural-text/35 shrink-0 tabular-nums">
                        {time
                          ? time.toLocaleTimeString(undefined, {
                              hour: '2-digit',
                              minute: '2-digit',
                              second: '2-digit',
                            })
                          : '--:--:--'}
                      </span>
                      <span className={`shrink-0 w-10 ${LEVEL_STYLE[ev.level] ?? ''}`}>
                        {ev.level.toUpperCase()}
                      </span>
                      <span className="text-natural-sage shrink-0 w-24 truncate">{ev.stage}</span>
                      <span className="text-natural-text/45 shrink-0 w-28 truncate">{ev.event}</span>
                      <span className={`min-w-0 break-words ${LEVEL_STYLE[ev.level] ?? ''}`}>
                        {ev.message}
                        {ev.duration_ms ? (
                          <span className="text-natural-text/35">
                            {' '}
                            ({(ev.duration_ms / 1000).toFixed(2)}s)
                          </span>
                        ) : null}
                      </span>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
